import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import HeaderSection from '../components/Shared/HeaderSection'
import GuidedTour from '../components/Shared/GuidedTour'
import CreateModeSelector from '../components/TicketCreate/CreateModeSelector'
import IncidentForm from '../components/Incident/IncidentForm'
import IncidentReportWizard from '../components/Incident/IncidentReportWizard'
import IncidentReportSuccess from '../components/Incident/IncidentReportSuccess'
import useReportIncident from '../hooks/useReportIncident'
import useIncidentFirstSteps from '../hooks/useIncidentFirstSteps'
import usePersistedState from '../hooks/usePersistedState'
import {
  INCIDENT_FORM_TOUR_STEPS,
  INCIDENT_REPORT_MODES,
  getIncidentReportMode,
} from '../utils/incidentReport'

/** Ejemplos de qué reportar aquí, para orientar a quien no es de TI. */
const EXAMPLES = [
  'Correo sospechoso, phishing o suplantación de identidad',
  'Contraseña comprometida, compartida o robada',
  'Pérdida o robo de un equipo, celular o USB corporativo',
  'Información confidencial enviada a la persona equivocada',
  'Virus, ransomware o comportamiento extraño del equipo',
  'Acceso no autorizado a un sistema, oficina o archivo',
]

/** Contexto para la IA a partir de lo enviado con el formulario avanzado. */
const contextFromPayload = (payload, assetIds, assets) => ({
  category: '',
  description: payload.Description,
  severity: payload.Severity,
  detectionSource: payload.DetectionSource || '',
  personalData: payload.PersonalDataAffected,
  assets: assets.filter((a) => assetIds.includes(a.Id)).map((a) => a.Name),
})

/**
 * Página abierta a cualquier colaborador autenticado para reportar un
 * incidente de seguridad de la información. Igual que /nuevo-ticket, ofrece
 * "paso a paso" (con primeros pasos generados con IA antes de enviar) o el
 * formulario avanzado (con tutorial). Al enviar, en ambos modos, se muestran
 * los pasos a seguir de inmediato.
 *
 * Sólo registra: el incidente entra como "Abierto" y el equipo de seguridad
 * hace el triage desde `/incidentes`. Deliberadamente no lista incidentes
 * existentes, para que la bitácora del SGSI quede restringida a soporte.
 *
 * @component
 * @returns {JSX.Element}
 */
const ReportIncident = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [lastMode, setLastMode] = usePersistedState(
    'reportIncident.lastMode',
    null
  )
  const report = useReportIncident()
  const firstSteps = useIncidentFirstSteps()
  /** Fuerza el remontaje del formulario o asistente al reportar otro. */
  const [formKey, setFormKey] = useState(0)
  const [tourOpen, setTourOpen] = useState(false)

  const mode = getIncidentReportMode(searchParams.get('modo'))
  const showSidebar = !!mode?.showSidebar && !report.created

  const selectMode = (key) => {
    setLastMode(key)
    setSearchParams({ modo: key })
  }

  /** Formulario avanzado: la guía se genera al enviar. */
  const handleFormSubmit = async (payload, assetIds) => {
    const ok = await report.submit(payload, assetIds)
    if (ok)
      firstSteps.generate(contextFromPayload(payload, assetIds, report.assets))
    return ok
  }

  const reportAnother = () => {
    report.clear()
    firstSteps.reset()
    setFormKey((key) => key + 1)
  }

  const content = report.created ? (
    <IncidentReportSuccess
      incident={report.created}
      firstSteps={firstSteps}
      onRetry={firstSteps.retry}
      onReportAnother={reportAnother}
    />
  ) : !mode ? (
    <CreateModeSelector
      modes={INCIDENT_REPORT_MODES}
      title="¿Cómo quiere reportar el incidente?"
      subtitle="Reportar a tiempo reduce el daño. Elija la opción con la que se sienta más cómodo."
      onSelect={selectMode}
      lastUsed={lastMode}
    />
  ) : mode.key === 'guiado' ? (
    <IncidentReportWizard
      key={formKey}
      assets={report.assets}
      author={report.author}
      saving={report.saving}
      firstSteps={firstSteps}
      onSubmit={report.submit}
    />
  ) : (
    <>
      {tourOpen && (
        <GuidedTour
          steps={INCIDENT_FORM_TOUR_STEPS}
          onClose={() => setTourOpen(false)}
        />
      )}
      <div className="ri-tour-bar">
        <span>
          <i className="fa-regular fa-lightbulb me-2" />
          ¿Primera vez reportando un incidente?
        </span>
        <button
          type="button"
          className="btn btn-sm ri-tour-btn"
          onClick={() => setTourOpen(true)}
        >
          <i className="fa-solid fa-circle-play me-2" />
          Ver tutorial
        </button>
      </div>
      <div className="ri-card p-3 p-md-4">
        <IncidentForm
          key={formKey}
          assets={report.assets}
          author={report.author}
          saving={report.saving}
          mode="report"
          submitLabel="Enviar reporte"
          onSubmit={handleFormSubmit}
        />
      </div>
    </>
  )

  return (
    <div className="report-incident-page container-fluid py-3">
      <HeaderSection
        compact
        title="Reportar incidente de seguridad"
        subtitle={
          mode
            ? `${mode.title} · avise de cualquier situación que ponga en riesgo la información`
            : 'Avise de cualquier situación que ponga en riesgo la información de la empresa'
        }
        icon="fa-solid fa-shield-halved"
        buttonText={mode && !report.created ? 'Cambiar modo' : null}
        buttonIcon="fa-solid fa-shuffle"
        buttonClass="btn btn-sm btn-outline-secondary"
        onButtonClick={() => setSearchParams({})}
      />

      <div className="row g-3">
        <div className={showSidebar ? 'col-12 col-lg-8' : 'col-12 ri-narrow'}>
          <div className="d-flex flex-column gap-3">{content}</div>
        </div>

        {/* GUÍA (sólo en el formulario avanzado) */}
        {showSidebar && (
          <div className="col-12 col-lg-4">
            <div className="ri-sidebar d-flex flex-column gap-3">
              <div className="ri-card p-3">
                <div className="ri-card-head">
                  <i className="fa-solid fa-circle-question" />
                  <span>¿Qué se reporta aquí?</span>
                </div>
                <ul className="ri-list">
                  {EXAMPLES.map((example) => (
                    <li key={example}>{example}</li>
                  ))}
                </ul>
              </div>

              <div className="ri-card p-3">
                <div className="ri-card-head">
                  <i className="fa-solid fa-circle-info" />
                  <span>¿No es de seguridad?</span>
                </div>
                <p className="small text-body-secondary mb-3">
                  Si lo suyo es una falla de un equipo, un programa o una
                  solicitud de soporte, abra un ticket normal.
                </p>
                <Link
                  to="/nuevo-ticket"
                  className="btn btn-sm btn-outline-primary w-100"
                >
                  <i className="fa-solid fa-plus me-2"></i>Crear un ticket
                </Link>
              </div>

              <div className="ri-card p-3">
                <div className="ri-card-head">
                  <i className="fa-solid fa-lock" />
                  <span>Reporte sin miedo</span>
                </div>
                <p className="small text-body-secondary mb-0">
                  Reportar a tiempo reduce el daño. Si duda si algo cuenta como
                  incidente, repórtelo igual: es preferible una falsa alarma a
                  un incidente que nadie vio.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .ri-narrow { max-width: 920px; margin: 0 auto; }
        .ri-card {
          background: var(--gm-surface, #fff);
          border: 1px solid var(--gm-border, #e2e8f0);
          border-radius: 14px;
        }
        .ri-card-head {
          display: flex; align-items: center; gap: 8px; margin-bottom: 12px;
          font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em;
          text-transform: uppercase; color: var(--gm-text-muted, #64748b);
        }
        .ri-card-head i { color: var(--gm-primary, #3b82f6); }
        .ri-list { margin: 0; padding-left: 1.1rem; font-size: 0.82rem; color: var(--gm-text-muted, #64748b); }
        .ri-list li { margin-bottom: 6px; }
        .ri-list li:last-child { margin-bottom: 0; }
        .ri-tour-bar {
          display: flex; align-items: center; justify-content: space-between; gap: 12px;
          padding: 10px 14px; border-radius: 12px; font-size: 0.85rem;
          border: 1px dashed var(--gm-border); color: var(--gm-text-muted);
        }
        .ri-tour-btn { flex-shrink: 0; border-radius: 9px; font-weight: 600; color: var(--gm-primary); background: var(--gm-primary-soft); }
        .ri-tour-btn:hover { color: #fff; background: var(--gm-primary); }
        @media (min-width: 992px) {
          .ri-sidebar { position: sticky; top: 1rem; }
        }
      `}</style>
    </div>
  )
}

export default ReportIncident
